import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MemoriesWallSectionProps {
  photos: string[];
  language: 'en' | 'mr';
}

export const MemoriesWallSection: React.FC<MemoriesWallSectionProps> = ({ photos, language }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!photos || photos.length === 0) return null;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="relative py-24 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400 mb-12">
          {language === 'mr' ? '📸 आमचे क्षण' : '📸 Our Memories Together'}
        </h2>

        <div className="relative group max-w-2xl mx-auto">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur-2xl opacity-75 group-hover:opacity-100 transition-opacity animate-pulse" />

          <div className="relative w-full bg-slate-900 rounded-2xl overflow-hidden border-4 border-slate-800 aspect-video">
            <img
              src={photos[currentIndex]}
              alt={`Memory ${currentIndex + 1}`}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-transparent to-transparent p-6">
              <p className="text-white text-lg font-bold">
                {language === 'mr' ? `स्मृती ${currentIndex + 1}` : `Memory ${currentIndex + 1}`}
              </p>
            </div>
            <div className="absolute top-4 right-4 bg-black/50 px-3 py-1 rounded-full text-sm text-blue-300 font-semibold">
              {currentIndex + 1} / {photos.length}
            </div>
          </div>

          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 p-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full hover:shadow-lg hover:shadow-blue-500/50 transition-all hover:scale-125"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 p-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg hover:shadow-pink-500/50 transition-all hover:scale-125"
          >
            <ChevronRight size={24} className="text-white" />
          </button>
        </div>

        <div className="mt-12 h-1 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-pink-500 transition-all"
            style={{ width: `${((currentIndex + 1) / photos.length) * 100}%` }}
          />
        </div>
      </div>
    </section>
  );
};
